// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Centralized query key definitions for purchase order-related queries.
 * All purchase order query keys should be defined here to ensure consistency.
 */

/**
 * Base query key for all purchase order queries.
 * Invalidating this key will invalidate all purchase order-related queries.
 */
export const purchaseOrdersQueryKey = ['purchaseOrders'] as const;

/**
 * Creates a query key for a purchase order list with optional filters.
 *
 * @param filter - Optional filter parameters
 * @returns Query key array for the purchase order list query
 */
export function purchaseOrderListQueryKey(filter?: unknown) {
  return filter ? [...purchaseOrdersQueryKey, filter] : purchaseOrdersQueryKey;
}

/**
 * Creates a query key for a single purchase order detail by key.
 *
 * @param purchaseOrderKey - The unique key of the purchase order
 * @returns Query key array for the purchase order detail query
 */
export function purchaseOrderDetailQueryKey(purchaseOrderKey: string) {
  return [...purchaseOrdersQueryKey, purchaseOrderKey] as const;
}

/**
 * Creates a query key for purchase order files.
 *
 * @param purchaseOrderKey - The unique key of the purchase order
 * @returns Query key array for the purchase order files query
 */
export function purchaseOrderFilesQueryKey(purchaseOrderKey: string) {
  return [...purchaseOrdersQueryKey, purchaseOrderKey, 'files'] as const;
}

/**
 * Creates a query key for purchase order URL links.
 *
 * @param purchaseOrderKey - The unique key of the purchase order
 * @returns Query key array for the purchase order URL links query
 */
export function purchaseOrderURLLinksQueryKey(purchaseOrderKey: string) {
  return [...purchaseOrdersQueryKey, purchaseOrderKey, 'url-link'] as const;
}

/**
 * Creates a query key for a purchase order's audit trail.
 *
 * @param purchaseOrderKey - The unique key of the purchase order
 * @returns Query key array for the purchase order audit query
 */
export function purchaseOrderAuditQueryKey(purchaseOrderKey: string) {
  return [...purchaseOrdersQueryKey, purchaseOrderKey, 'audit'] as const;
}
